import { computed } from 'mobx'
import { AccountMergeRequestSearchViewModel } from './AccountMergeRequestSearchViewModel'
import { type AccountMergeRequestStore } from './AccountMergeRequestStore'
import { type AccountMergeRequestDialogs } from './AccountMergeRequestItemViewModel'
import { MergeAccountsDialogViewModel } from '@/features/admin/account-linking/MergeAccountsDialog/MergeAccountsDialogViewModel'
import { RejectMergeDialogViewModel } from '@/features/admin/account-linking/RejectMergeDialog/RejectMergeDialogViewModel'
import { IgnoreMergeRequestDialogViewModel } from '@/features/admin/account-linking/IgnoreMergeRequestDialog/IgnoreMergeRequestDialogViewModel'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

export class AccountMergeRequestsViewModel
  implements ActivationLifecycle, AccountMergeRequestDialogs
{
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Searcher for merge requests.
   */
  readonly searcher: AccountMergeRequestSearchViewModel

  /**
   * View Model for the "Merge Accounts" dialog.
   */
  readonly mergeAccountsDialogVm

  /**
   * View Model for the "Reject Merge Request" dialog.
   */
  readonly rejectMergeDialogVm

  /**
   * View Model for the "Ignore Merge Request" dialog.
   */
  readonly ignoreMergeRequestVm

  constructor(
    private readonly _store: AccountMergeRequestStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _hubspotPortalId: string,
  ) {
    this.searcher = new AccountMergeRequestSearchViewModel(
      this._store,
      this._createAlertRetryWrapper,
      this,
      this._hubspotPortalId,
    )

    this.mergeAccountsDialogVm = new MergeAccountsDialogViewModel(
      this._store,
      this.searcher.refreshItems,
      this._newAlert,
      this._createAlertErrorHandler,
    )

    this.rejectMergeDialogVm = new RejectMergeDialogViewModel(
      this._store,
      this.searcher.refreshItems,
      this._newAlert,
      this._createAlertErrorHandler,
    )

    this.ignoreMergeRequestVm = new IgnoreMergeRequestDialogViewModel(
      this._store,
      this.searcher.refreshItems,
      this._newAlert,
      this._createAlertErrorHandler,
    )

    this._lifecycle.manage(this.searcher)
  }

  /**
   * The total number of merge requests.
   */
  @computed
  get total() {
    return this.searcher.mergeRequests.length
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }
}
